'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { getDailyHoroscope, DailyHoroscopeRecord, HoroscopeParsedContent } from '@/lib/api/client';
import { HoroscopeTabPeriod } from '@/lib/utils/horoscope-format';
import { ZODIAC_METADATA } from '@/lib/config/zodiac';
import { ArrowLeft, Sparkles, Compass, Briefcase, Heart, Activity, ShieldAlert } from 'lucide-react';

export default function SignHoroscopeDetailPage({ params }: { params: { sign: string } }) {
  const searchParams = useSearchParams();
  const rawPeriod = searchParams?.get('period') || 'daily';
  const period: HoroscopeTabPeriod = (['daily', 'weekly', 'monthly', 'yearly'].includes(rawPeriod)
    ? rawPeriod
    : 'daily') as HoroscopeTabPeriod;

  const signKey = (params?.sign || 'aries').toLowerCase();
  const zodiac = ZODIAC_METADATA.find((z) => z.sign === signKey) || ZODIAC_METADATA[0];

  const [record, setRecord] = useState<DailyHoroscopeRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getDailyHoroscope(signKey, period)
      .then((res) => setRecord(res))
      .catch((err) => setError(err.message || `Failed to load ${period} horoscope`))
      .finally(() => setLoading(false));
  }, [signKey, period]);

  let parsed: HoroscopeParsedContent = {};
  if (record?.content) {
    try {
      parsed = typeof record.content === 'string' ? JSON.parse(record.content) : record.content;
    } catch {
      parsed = { summary: record.content };
    }
  }

  const capitalizedPeriod = period.charAt(0).toUpperCase() + period.slice(1);

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-6 px-4 sm:px-6">
      {/* Navigation Back Link */}
      <Link
        href="/horoscope/today-horoscope"
        className="inline-flex items-center gap-2 text-xs font-semibold text-[#5ab4ff] hover:text-[#b9c6ff] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to All Zodiac Signs</span>
      </Link>

      {/* Header Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#5ab4ff]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
          <div className="flex items-center gap-4">
            <span className="text-5xl text-white">{zodiac.symbol}</span>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-3xl font-['Lexend'] font-medium text-white">{zodiac.name}</h1>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-white/[0.05] border border-white/12 text-[#5ab4ff] font-medium">
                  {zodiac.sanskrit}
                </span>
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-[#ffd36b]/15 border border-[#ffd36b]/30 text-[#ffd36b] font-semibold uppercase">
                  {capitalizedPeriod}
                </span>
              </div>
              <p className="text-xs text-[#dcecff]/70 font-['Outfit']">{zodiac.dates} : Element {zodiac.element}</p>
            </div>
          </div>

          <div className="text-left sm:text-right">
            <span className="text-xs text-[#dcecff]/60 block font-['Outfit']">Horoscope Date / Period</span>
            <span className="text-sm font-medium text-white font-mono">
              {record?.horoscopeDate || 'Current Period (IST)'}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-8 text-center text-[#dcecff]/60 animate-pulse font-['Outfit']">
          Generating {period} transit insights for {zodiac.name}...
        </div>
      ) : error ? (
        <div className="bg-[#ff9bb0]/10 border border-[#ff9bb0]/30 rounded-3xl p-6 text-[#ff9bb0] text-sm">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary Overview Card */}
          {parsed.summary && (
            <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-7 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl">
              <div className="flex items-center gap-2 text-[#5ab4ff] text-xs font-semibold uppercase tracking-wider mb-2">
                <Sparkles className="w-4 h-4" />
                <span>{capitalizedPeriod} Overview</span>
              </div>
              <p className="text-white text-base leading-relaxed font-['Lexend'] font-normal">
                {parsed.summary}
              </p>
            </div>
          )}

          {/* Structured Guidance Sections Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Outlook */}
            <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-2">
              <div className="flex items-center gap-2 text-[#b9c6ff] text-sm font-semibold font-['Lexend']">
                <Compass className="w-4 h-4 text-[#b9c6ff]" />
                <span>General Outlook</span>
              </div>
              <p className="text-[#dcecff]/75 text-sm leading-relaxed font-['Outfit']">
                {parsed.sections?.general || `Positive transits encourage balanced momentum during this ${period} period.`}
              </p>
            </div>

            {/* Career & Focus */}
            <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-2">
              <div className="flex items-center gap-2 text-[#ffd36b] text-sm font-semibold font-['Lexend']">
                <Briefcase className="w-4 h-4 text-[#ffd36b]" />
                <span>Career & Focus</span>
              </div>
              <p className="text-[#dcecff]/75 text-sm leading-relaxed font-['Outfit']">
                {parsed.sections?.career || 'Focus on strategic tasks and clear communication.'}
              </p>
            </div>

            {/* Relationships & Harmony */}
            <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-2">
              <div className="flex items-center gap-2 text-[#ff9bb0] text-sm font-semibold font-['Lexend']">
                <Heart className="w-4 h-4 text-[#ff9bb0]" />
                <span>Relationships & Harmony</span>
              </div>
              <p className="text-[#dcecff]/75 text-sm leading-relaxed font-['Outfit']">
                {parsed.sections?.relationships || 'Open dialogue fosters understanding in personal ties.'}
              </p>
            </div>

            {/* Health & Vitality */}
            <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-xl space-y-2">
              <div className="flex items-center gap-2 text-[#8dffc9] text-sm font-semibold font-['Lexend']">
                <Activity className="w-4 h-4 text-[#8dffc9]" />
                <span>Health & Vitality</span>
              </div>
              <p className="text-[#dcecff]/75 text-sm leading-relaxed font-['Outfit']">
                {parsed.sections?.health || 'Prioritize rest and hydration for optimal energy.'}
              </p>
            </div>
          </div>

          {/* Standard Disclaimer Footer */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 flex items-start gap-3 text-xs text-[#dcecff]/70 font-['Outfit']">
            <ShieldAlert className="w-4 h-4 text-[#ffd36b] shrink-0 mt-0.5" />
            <p>
              {parsed.disclaimer || 'Horoscopes are interpretive astronomical summaries based on Vedic transit positions. They do not constitute guaranteed predictions or medical, legal, or financial advice.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
